import { useState, useRef, useEffect, useCallback } from "react";
import { io } from "socket.io-client";
import { getDistance } from "geolib";
import api from "../services/api";
import { meetingService } from "../services/meetingService";

export default function useMeeting(user) {
  const [meetingStatus, setMeetingStatus] = useState("idle");
  const [meeting, setMeeting] = useState(null);
  const [myPosition, setMyPosition] = useState(null);
  const [otherPosition, setOtherPosition] = useState(null);
  const [otherUserId, setOtherUserId] = useState(null);
  const [meetingRequest, setMeetingRequest] = useState(null);
  const [error, setError] = useState(null);

  const socketRef = useRef(null);
  const watchIdRef = useRef(null);
  const statusRef = useRef("idle");
  const meetingRef = useRef(null);
  const lastEmitRef = useRef(0);
  const startGPSRef = useRef(null);

  useEffect(() => {
    statusRef.current = meetingStatus;
  }, [meetingStatus]);

  useEffect(() => {
    meetingRef.current = meeting;
  }, [meeting]);

  const cleanupGPS = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  const resetState = useCallback(() => {
    setMeetingStatus("idle");
    setMeeting(null);
    setMyPosition(null);
    setOtherPosition(null);
    setOtherUserId(null);
    setMeetingRequest(null);
    setError(null);
  }, []);

  const startGPS = useCallback((socket) => {
    if (!navigator.geolocation) {
      setError("GPS not available on this device");
      return;
    }
    const onPosition = (position) => {
      const pos = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
      setMyPosition(pos);
      const now = Date.now();
      if (socket.connected && now - lastEmitRef.current > 3000) {
        socket.emit("location:update", {
          meetingId: meetingRef.current?.meetingId,
          latitude: pos.latitude,
          longitude: pos.longitude,
        });
        lastEmitRef.current = now;
      }
    };
    watchIdRef.current = navigator.geolocation.watchPosition(onPosition, null, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 10000,
    });
  }, []);

  startGPSRef.current = startGPS;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !user) return;

    const baseURL = api.defaults.baseURL || "";
    const origin = baseURL.replace(/\/?api\/?$/i, "") || baseURL;

    const socket = io(origin, {
      auth: { token },
      transports: ["websocket", "polling"],
    });

    socket.on("meeting:request", (data) => {
      setMeetingRequest(data);
    });

    socket.on("meeting:accepted", (data) => {
      setMeetingStatus("joining");
      if (socketRef.current?.connected) {
        socketRef.current.emit("meeting:join", { meetingId: data.meetingId });
      }
    });

    socket.on("meeting:rejected", () => {
      setMeetingStatus("idle");
      setError("Meeting request was declined");
      setMeeting(null);
    });

    socket.on("meeting:joined", (data) => {
      setMeetingStatus("active");
      if (data.otherUserId) setOtherUserId(data.otherUserId);
      if (data.otherUser?.latitude) {
        setOtherPosition({
          latitude: data.otherUser.latitude,
          longitude: data.otherUser.longitude,
        });
      }
      startGPSRef.current?.(socket);
    });

    socket.on("participant:left", () => {
      setMeetingStatus("ended");
    });

    socket.on("participant:offline", () => {
      if (statusRef.current === "active" || statusRef.current === "arrived") {
        setMeetingStatus("offline");
      }
    });

    socket.on("participant:online", () => {
      if (statusRef.current === "offline") {
        setMeetingStatus("active");
      }
    });

    socket.on("location:changed", (data) => {
      if (data.latitude && data.longitude) {
        setOtherPosition({
          latitude: data.latitude,
          longitude: data.longitude,
        });
      }
    });

    socket.on("meeting:ended", () => {
      setMeetingStatus("ended");
      cleanupGPS();
    });

    socket.on("error", (err) => {
      setError(err.message || "Socket error");
      setMeetingStatus("idle");
    });

    socket.on("connect", () => {
      if (
        meetingRef.current?.meetingId &&
        statusRef.current !== "idle" &&
        statusRef.current !== "ended"
      ) {
        socket.emit("meeting:join", { meetingId: meetingRef.current.meetingId });
      }
    });

    socketRef.current = socket;

    return () => {
      cleanupGPS();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      meetingStatus !== "active" &&
      meetingStatus !== "arrived" &&
      meetingStatus !== "offline"
    )
      return;
    if (!myPosition || !otherPosition) return;

    try {
      const distance = getDistance(
        { latitude: myPosition.latitude, longitude: myPosition.longitude },
        { latitude: otherPosition.latitude, longitude: otherPosition.longitude }
      );
      if (distance < 20 && statusRef.current === "active") {
        setMeetingStatus("arrived");
      }
    } catch {
      // ignore geolib errors
    }
  }, [myPosition, otherPosition, meetingStatus]);

  const requestMeeting = useCallback(async (participantId, creatorInfo) => {
    if (statusRef.current !== "idle") {
      const current = meetingRef.current;
      if (current?.meetingId) {
        try { await meetingService.endMeeting(current.meetingId); } catch {}
      }
      socketRef.current?.emit("meeting:leave", { meetingId: current?.meetingId });
      cleanupGPS();
    }
    setMeetingRequest(null);
    setMyPosition(null);
    setOtherPosition(null);
    setOtherUserId(null);
    setError(null);
    setMeeting(null);
    setMeetingStatus("requesting");
    try {
      const result = await meetingService.createMeeting(participantId);
      const meetingData = result.data || result;
      setMeeting(meetingData);
      setOtherUserId(participantId);
      if (socketRef.current?.connected) {
        socketRef.current.emit("meeting:request", {
          to: participantId,
          meetingId: meetingData.meetingId,
          creator: creatorInfo,
        });
      }
      return meetingData;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to start meeting");
      setMeetingStatus("idle");
      throw err;
    }
  }, [cleanupGPS]);

  const acceptMeeting = useCallback((meetingId, from) => {
    setMeetingRequest(null);
    setMeetingStatus("joining");
    setMeeting({ meetingId });
    if (socketRef.current?.connected) {
      socketRef.current.emit("meeting:accept", { meetingId, from });
      socketRef.current.emit("meeting:join", { meetingId });
    }
  }, []);

  const rejectMeeting = useCallback((meetingId, from) => {
    setMeetingRequest(null);
    if (socketRef.current?.connected) {
      socketRef.current.emit("meeting:reject", { meetingId, from });
    }
  }, []);

  const joinMeeting = useCallback((meetingId) => {
    setMeetingStatus("joining");
    setMeeting({ meetingId });
    if (socketRef.current?.connected) {
      socketRef.current.emit("meeting:join", { meetingId });
    }
  }, []);

  const endMeeting = useCallback(async () => {
    const currentMeeting = meetingRef.current;
    if (currentMeeting?.meetingId) {
      try {
        await meetingService.endMeeting(currentMeeting.meetingId);
      } catch {
        // ignore
      }
    }
    setMeetingStatus("ended");
    cleanupGPS();
  }, [cleanupGPS]);

  const leaveMeeting = useCallback(() => {
    const currentMeeting = meetingRef.current;
    if (socketRef.current?.connected) {
      socketRef.current.emit("meeting:leave", {
        meetingId: currentMeeting?.meetingId,
      });
    }
    cleanupGPS();
    resetState();
  }, [cleanupGPS, resetState]);

  const clearMeetingRequest = useCallback(() => setMeetingRequest(null), []);

  const clearError = useCallback(() => setError(null), []);

  return {
    meetingStatus,
    meeting,
    meetingRequest,
    myPosition,
    otherPosition,
    otherUserId,
    error,
    requestMeeting,
    acceptMeeting,
    rejectMeeting,
    joinMeeting,
    endMeeting,
    leaveMeeting,
    clearMeetingRequest,
    clearError,
  };
}
